import React from 'react';
import type {
  HighQualityShortDramaTable,
} from '$/api/server.types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../Table';
import styles from './index.module.scss';

export interface CompareShortDramasTableProps {
  tableData?: HighQualityShortDramaTable;
}

type CompareTableRow = Record<string, string | undefined>;

// getDataKey 对齐 useSubscribeVipProductList 的 deepKeysToCamelCase 转换结果。
function getDataKey(key: string) {
  return key.replace(/([-_][a-z])/gi, ($1) => {
    return $1.toUpperCase().replace(/[-_]/g, '');
  });
}

// getStringCell 从表格行里安全取值，避免空字段把表格撑出 undefined。
function getStringCell(row: CompareTableRow, key: string) {
  return row[getDataKey(key)] || '-';
}

// isRoboneoColumn 识别设计稿里的 RoboNeo Agent 高亮列。
function isRoboneoColumn(key: string, label: string, index: number) {
  return index === 1 || getDataKey(key) === 'roboneoAgent' || label.toLowerCase().includes('roboneo');
}

// getHeaderClassName 根据真实表头位置选择维度列、RoboNeo 高亮列或普通方案列。
function getHeaderClassName(key: string, label: string, index: number) {
  if (index === 0) return styles.dimensionHead;
  if (isRoboneoColumn(key, label, index)) return styles.roboneoHead;
  return styles.optionHead;
}

// getCellClassName 保持表体和表头列语义一致。
function getCellClassName(key: string, label: string, index: number) {
  if (index === 0) return styles.dimensionCell;
  if (isRoboneoColumn(key, label, index)) return styles.roboneoCell;
  return styles.optionCell;
}

// CompareShortDramasTable 渲染高质量短剧成本对比表。
function CompareShortDramasTable({ tableData }: CompareShortDramasTableProps) {
  const headers = tableData?.headers || [];
  const rows = (tableData?.items || []) as CompareTableRow[];
  if (!headers.length || !rows.length) return null;

  return (
    <section className={styles.wrapper}>
      <div className={styles.title}>
        {/* 保留接口标题优先级；兜底文案暂用中文，待 Smartcat 提供翻译 key 后替换。 */}
        {tableData?.title || '从一次性交付，到持续经营 IP'}
      </div>
      <Table containerClassName={styles.tableContainer} className={styles.compareTable}>
        <TableHeader>
          <TableRow>
            {headers.map((header, index) => (
              <TableHead
                key={header.key}
                className={getHeaderClassName(header.key, header.label, index)}
              >
                {header.label}
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row, rowIndex) => (
            // eslint-disable-next-line react/no-array-index-key
            <TableRow key={`${row.dimension || 'compare-row'}-${rowIndex}`} className={styles.compareRow}>
              {headers.map((header, index) => (
                <TableCell
                  key={`${header.key}-${row.dimension || rowIndex}`}
                  className={getCellClassName(header.key, header.label, index)}
                >
                  {getStringCell(row, header.key)}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </section>
  );
}

export default CompareShortDramasTable;
